/* Adaptive page that displays loading based on state,
used in top-level providers during data hydration (before router renders) */

// absolute imports
import PropTypes from 'prop-types';
import { makeStyles } from 'tss-react/mui';
import { Grid, useTheme, ThemeProvider } from '@mui/material';
import { keyframes } from 'tss-react';

// relative imports
import PrimaryLayout from '../layouts/PrimaryLayout';
import PageHeader from './PageHeader';
import PageBody from './PageBody';
import Loader from '../custom-components/Loader';
import LedgibleIconSVG from '../img-assets/LedgibleIcon.svg';
import { getAppTitle } from '../utilities/appUtilities';
import {
  getAppConfig,
  getAppTheme,
} from '../../../data/app-config/configs/configUtilities';

const useStyles = makeStyles()((theme) => ({
  splashScreen: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.default,
  },
  loadingDot: {
    marginTop: '40px',
    position: 'relative',
    height: '15px',
    width: '15px',
    borderRadius: '50%',
    display: 'inline-block',
    animation: `${keyframes`
      0% {
        left: -80px;
        background-color: #eee;
      }
      50% {
        background-color: ${theme.palette.primary.main};
      }
      100% {
        left: 80px;
        background-color: #eee;
      },
    `} 1.2s ${theme.transitions.easing.easeInOut} infinite alternate`,
  },
}));

const AnimatedLedgibleLoadingImage = () => {
  const { appConfig } = getAppConfig();
  const { classes } = useStyles();

  return (
    <div className={classes.splashScreen} data-cy="ledgible-cylon-animation">
      <img
        src={appConfig?.appLoaderIconImgSrc || LedgibleIconSVG}
        alt={`${getAppTitle()} Icon`}
        height="100px"
        data-cy="cylon-logo"
      />
      <div className={classes.loadingDot} />
    </div>
  );
};

const LoadingPage = ({
  loadingApplication,
  loadingLayout,
  loadingPage,
  hidePageHeader,
  hideSecondaryMenu,
  accountOptional,
}) => {
  const theme = useTheme();
  // Check for whiteLabel property, which theme won't have (mui default) if ThemeProvider not present
  const isPreRouter = !theme.whiteLabel;

  const renderContent = () => {
    if (loadingApplication) {
      return <AnimatedLedgibleLoadingImage />;
    }
    if (!loadingApplication && loadingLayout) {
      return (
        <PrimaryLayout hideSecondaryMenu accountOptional showLoading>
          <Grid
            item
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            <Loader fillSpace />
          </Grid>
        </PrimaryLayout>
      );
    }
    if (!loadingApplication && !loadingLayout && loadingPage) {
      return (
        <PrimaryLayout
          hideSecondaryMenu={hideSecondaryMenu}
          hideHeader={hidePageHeader}
          accountOptional={accountOptional}
          showLoading
        >
          <PageHeader pageTitle="" />
          <PageBody>
            <Grid item sx={{ minHeight: '60vh' }}>
              <Loader marginTop={10} />
            </Grid>
          </PageBody>
        </PrimaryLayout>
      );
    }
    return null;
  };

  // If we don't have a router context, use window.appTheme
  if (isPreRouter) {
    return (
      <ThemeProvider theme={getAppTheme()}>{renderContent()}</ThemeProvider>
    );
  }

  // Otherwise, render with the theme from context
  return renderContent();
};

export default LoadingPage;

LoadingPage.propTypes = {
  loadingApplication: PropTypes.bool,
  loadingLayout: PropTypes.bool,
  loadingPage: PropTypes.bool,
  hidePageHeader: PropTypes.bool,
  hideSecondaryMenu: PropTypes.bool,
  accountOptional: PropTypes.bool,
};

LoadingPage.defaultProps = {
  loadingApplication: false,
  loadingLayout: false,
  loadingPage: false,
  hidePageHeader: false,
  hideSecondaryMenu: false,
  accountOptional: false,
};
